'use client';

import { useEffect, useState, useCallback, useMemo, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import {
  marketplaceApi,
  storefrontApi,
  type BlockedBusiness,
  type StoreFeedItem,
  type ProductFeedItem,
} from '@/lib/api';
import { useCurrentBusiness } from '@/lib/business-context';
import { StoreCard } from '@/components/marketplace/store-card';
import { ProductCard } from '@/components/marketplace/product-card';
import {
  MarketplaceFilterBar,
  coerceSort,
  defaultSortForView,
  type MarketplaceView,
  type MarketplaceSort,
} from '@/components/marketplace/filter-bar';
import { Button } from '@/components/ui/button';
import { Loader2, Store, Package } from 'lucide-react';
import { PULL_REFRESH_EVENT } from '@/components/native/pull-to-refresh';
import { APP_RESUME_EVENT } from '@/lib/native/events';

const MARKETPLACE_REFRESH_EVENT = 'marketplace:refresh';

/** Two-up on phones so twice as many stores/products are visible per screen. */
const GRID_CLASS = 'grid gap-3 grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4';

function EmptyState({
  icon,
  message,
  onClear,
}: {
  icon: React.ReactNode;
  message: string;
  onClear?: () => void;
}) {
  return (
    <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
      {icon}
      <p className="text-sm">{message}</p>
      {onClear && (
        <Button variant="ghost" size="sm" className="mt-2 h-8 text-xs" onClick={onClear}>
          Clear filter
        </Button>
      )}
    </div>
  );
}

export default function MarketplacePage({ initialStores = null }: { initialStores?: StoreFeedItem[] | null }) {
  return (
    <Suspense>
      <MarketplaceContent initialStores={initialStores} />
    </Suspense>
  );
}

function MarketplaceContent({ initialStores }: { initialStores: StoreFeedItem[] | null }) {
  const { businesses, setCurrentBusinessId } = useCurrentBusiness();
  const [stores, setStores] = useState<StoreFeedItem[]>(initialStores ?? []);
  const [loading, setLoading] = useState(initialStores === null);
  const [products, setProducts] = useState<ProductFeedItem[]>([]);
  const [blocked, setBlocked] = useState<BlockedBusiness[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const searchParams = useSearchParams();
  const router = useRouter();
  const searchQuery = (searchParams.get('q') ?? '').trim();
  const normalizedSearchQuery = searchQuery.toLowerCase();

  // View / filter / sort live in the URL: a filtered marketplace is then
  // shareable, survives back-navigation, and is preserved by the top bar's
  // search submit (which merges into the existing params).
  const view: MarketplaceView = searchParams.get('view') === 'products' ? 'products' : 'stores';
  const activeCategory = searchParams.get('cat');
  const sort = coerceSort(view, searchParams.get('sort'));

  const setParams = useCallback(
    (patch: Record<string, string | null>) => {
      const params = new URLSearchParams(searchParams.toString());
      for (const [key, value] of Object.entries(patch)) {
        if (value == null || value === '') params.delete(key);
        else params.set(key, value);
      }
      const qs = params.toString();
      router.replace(qs ? `/marketplace?${qs}` : '/marketplace', { scroll: false });
    },
    [router, searchParams],
  );

  const handleViewChange = useCallback(
    (next: MarketplaceView) => {
      // Category keys and sort values are per-view; carrying them across
      // would silently filter everything out.
      setParams({ view: next === 'stores' ? null : next, cat: null, sort: null });
    },
    [setParams],
  );

  const handleSortChange = useCallback(
    (next: MarketplaceSort) => {
      setParams({ sort: next === defaultSortForView(view) ? null : next });
    },
    [setParams, view],
  );

  const handleCategoryChange = useCallback(
    (next: string | null) => setParams({ cat: next }),
    [setParams],
  );

  const fetchStores = useCallback(async () => {
    setLoading(true);
    try {
      const timeoutMs = 12_000;
      const timeoutPromise = new Promise<{ items: StoreFeedItem[]; nextCursor?: string }>((_, reject) =>
        setTimeout(() => reject(new Error('Stores feed timeout')), timeoutMs),
      );
      const response = await Promise.race([
        storefrontApi.getStoresFeed(),
        timeoutPromise,
      ]);
      const items = Array.isArray(response)
        ? response
        : (response && Array.isArray((response as any).items) ? (response as any).items : []);
      setStores(items);
    } catch {
      setStores([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialStores === null) void fetchStores();
  }, [fetchStores, initialStores]);

  // The public feed endpoints are unauthenticated and cannot know who is
  // looking, so blocked sellers are filtered out here instead. Gated on having
  // a business: this page is public, and a signed-out visitor would otherwise
  // spend a guaranteed 401 (plus a session refresh) on every load.
  const hasBusiness = businesses.length > 0;
  useEffect(() => {
    if (!hasBusiness) {
      setBlocked([]);
      return;
    }
    let cancelled = false;
    marketplaceApi
      .getBlockedBusinesses()
      .then((rows) => {
        if (!cancelled) setBlocked(rows);
      })
      .catch(() => {
        if (!cancelled) setBlocked([]);
      });
    return () => {
      cancelled = true;
    };
  }, [hasBusiness]);

  const blockedIds = useMemo(() => new Set(blocked.map((b) => b.id)), [blocked]);

  const visibleStores = useMemo(
    () => (blockedIds.size === 0 ? stores : stores.filter((s) => !blockedIds.has(s.id))),
    [stores, blockedIds],
  );
  const visibleProducts = useMemo(
    () =>
      blockedIds.size === 0
        ? products
        : products.filter((p) => !(p.sellerBusiness?.id && blockedIds.has(p.sellerBusiness.id))),
    [products, blockedIds],
  );

  // City '' asks the API for every city; the feed is capped server-side.
  const fetchProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const items = await storefrontApi.getProductsFeed('', undefined, 100);
      setProducts(Array.isArray(items) ? items : []);
    } catch {
      setProducts([]);
    } finally {
      setProductsLoading(false);
      setProductsLoaded(true);
    }
  }, []);

  // Only pay for the products feed when someone actually opens that view.
  useEffect(() => {
    if (view === 'products' && !productsLoaded && !productsLoading) void fetchProducts();
  }, [view, productsLoaded, productsLoading, fetchProducts]);

  const myBusinessIds = useMemo(
    () => new Set(businesses.map((business) => business.id)),
    [businesses],
  );

  const myBusinessBySlug = useMemo(() => {
    const map = new Map<string, string>();
    businesses.forEach((business) => {
      if (business.slug) map.set(business.slug, business.id);
    });
    return map;
  }, [businesses]);

  useEffect(() => {
    const storeParam = searchParams.get('store');
    const productParam = searchParams.get('product');
    if (!storeParam) return;

    const ownedBusinessId = myBusinessBySlug.get(storeParam);
    if (ownedBusinessId) setCurrentBusinessId(ownedBusinessId);

    if (productParam) {
      router.replace(`/s/${storeParam}/p/${productParam}`, { scroll: false });
      return;
    }
    router.replace(`/s/${storeParam}`, { scroll: false });
  }, [myBusinessBySlug, router, searchParams, setCurrentBusinessId]);


  const searchedStores = useMemo(
    () =>
      searchQuery
        ? visibleStores.filter(
          (s) =>
            s.name.toLowerCase().includes(normalizedSearchQuery) ||
            (s.category?.toLowerCase().includes(normalizedSearchQuery) ?? false) ||
            (s.bio?.toLowerCase().includes(normalizedSearchQuery) ?? false)
        )
        : visibleStores,
    [visibleStores, searchQuery, normalizedSearchQuery],
  );

  const searchedProducts = useMemo(
    () =>
      searchQuery
        ? visibleProducts.filter(
          (p) =>
            p.title.toLowerCase().includes(normalizedSearchQuery) ||
            (p.category?.toLowerCase().includes(normalizedSearchQuery) ?? false) ||
            (p.description?.toLowerCase().includes(normalizedSearchQuery) ?? false) ||
            (p.sellerBusiness?.name?.toLowerCase().includes(normalizedSearchQuery) ?? false)
        )
        : visibleProducts,
    [visibleProducts, searchQuery, normalizedSearchQuery],
  );

  /** Category chips are built from what is actually in the current view. */
  const categoryOptions = useMemo(() => {
    const counts = new Map<string, number>();
    const source: (string | null | undefined)[] =
      view === 'stores'
        ? searchedStores.map((s) => s.category)
        : searchedProducts.map((p) => p.category);
    for (const raw of source) {
      const key = raw?.trim();
      if (!key) continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
  }, [view, searchedStores, searchedProducts]);

  const categoryMatchedStores = useMemo(
    () => (activeCategory ? searchedStores.filter((s) => s.category === activeCategory) : searchedStores),
    [searchedStores, activeCategory],
  );

  const filteredStores = useMemo(() => {
    const list = [...categoryMatchedStores];
    switch (sort) {
      case 'rating':
        // Unrated stores sort last rather than as zero.
        return list.sort((a, b) => (b.avgRating ?? -1) - (a.avgRating ?? -1) || b.ratingCount - a.ratingCount);
      case 'products':
        return list.sort((a, b) => b.productCount - a.productCount);
      case 'name':
        return list.sort((a, b) => a.name.localeCompare(b.name));
      default:
        return list;
    }
  }, [categoryMatchedStores, sort]);

  const filteredProducts = useMemo(() => {
    const list = activeCategory
      ? searchedProducts.filter((p) => p.category === activeCategory)
      : [...searchedProducts];
    switch (sort) {
      case 'price_asc':
        return list.sort((a, b) => Number(a.price) - Number(b.price));
      case 'price_desc':
        return list.sort((a, b) => Number(b.price) - Number(a.price));
      case 'name':
        return list.sort((a, b) => a.title.localeCompare(b.title));
      default:
        return list; // the API already returns newest first
    }
  }, [searchedProducts, activeCategory, sort]);

  const claimedStores = useMemo(
    () => filteredStores.filter((store) => store.claimStatus !== 'unclaimed'),
    [filteredStores],
  );
  const myStores = useMemo(
    () => claimedStores.filter((store) => myBusinessIds.has(store.id)),
    [claimedStores, myBusinessIds],
  );
  const claimedOtherStores = useMemo(
    () => claimedStores.filter((store) => !myBusinessIds.has(store.id)),
    [claimedStores, myBusinessIds],
  );
  const unclaimedStores = useMemo(
    () => filteredStores.filter((store) => store.claimStatus === 'unclaimed' && !myBusinessIds.has(store.id)),
    [filteredStores, myBusinessIds],
  );

  const handleMyStoreClick = useCallback(
    (store: StoreFeedItem) => {
      setCurrentBusinessId(store.id);
    },
    [setCurrentBusinessId],
  );

  useEffect(() => {
    const handleMarketplaceRefresh = () => {
      void fetchStores();
    };
    window.addEventListener(MARKETPLACE_REFRESH_EVENT, handleMarketplaceRefresh);
    // Pull-to-refresh and returning to the app from the background refetch too.
    window.addEventListener(PULL_REFRESH_EVENT, handleMarketplaceRefresh);
    window.addEventListener(APP_RESUME_EVENT, handleMarketplaceRefresh);
    return () => {
      window.removeEventListener(MARKETPLACE_REFRESH_EVENT, handleMarketplaceRefresh);
      window.removeEventListener(PULL_REFRESH_EVENT, handleMarketplaceRefresh);
      window.removeEventListener(APP_RESUME_EVENT, handleMarketplaceRefresh);
    };
  }, [fetchStores]);

  return (
    // Single scroll container: `main` (the app shell) scrolls this page. The
    // negative margins only cancel the shell's side/top gutters so the filter
    // bar and grid run edge to edge; the bottom gutter stays so the last row
    // clears the tab bar. A second overflow-y-auto here made both `main` and
    // the grid scroll on phones (double scroll) and broke pull-to-refresh.
    <div className="flex flex-col -mx-4 -mt-4 md:-mx-6 md:-mt-6">
      {/* Secondary header: category filters + sort, with the Stores/Products
          toggle pinned right. Sticks under the top bar at every breakpoint. */}
      <MarketplaceFilterBar
        view={view}
        onViewChange={handleViewChange}
        storeCount={searchedStores.length}
        productCount={productsLoaded ? searchedProducts.length : 0}
        categories={categoryOptions}
        activeCategory={activeCategory}
        onCategoryChange={handleCategoryChange}
        sort={sort}
        onSortChange={handleSortChange}
      />

      {/* Main Content — Stores or Products, two-up on phones */}
      {loading || (view === 'products' && productsLoading && !productsLoaded) ? (
        <div className="flex min-h-[50dvh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : view === 'products' ? (
        <div className="p-4 md:p-6">
          {filteredProducts.length === 0 ? (
            <EmptyState
              icon={<Package className="mb-3 h-10 w-10 opacity-40" />}
              message={
                searchQuery || activeCategory ? 'No products match these filters.' : 'No products yet.'
              }
              onClear={activeCategory ? () => handleCategoryChange(null) : undefined}
            />
          ) : (
            <div className={GRID_CLASS}>
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 md:p-6">
          {filteredStores.length === 0 ? (
            <EmptyState
              icon={<Store className="mb-3 h-10 w-10 opacity-40" />}
              message={
                searchQuery || activeCategory ? 'No stores match these filters.' : 'No stores yet.'
              }
              onClear={activeCategory ? () => handleCategoryChange(null) : undefined}
            />
          ) : (
            <div className="space-y-8">
              {(myStores.length > 0 || claimedOtherStores.length > 0) && (
                <div className={GRID_CLASS}>
                  {myStores.map((store, index) => (
                    <StoreCard
                      key={store.id}
                      store={store}
                      onClick={() => handleMyStoreClick(store)}
                      isOwnStore
                      priority={index < 2}
                    />
                  ))}
                  {claimedOtherStores.map((store, index) => (
                    <StoreCard key={store.id} store={store} priority={myStores.length + index < 2} />
                  ))}
                </div>
              )}
              {unclaimedStores.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Unclaimed businesses
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <div className={GRID_CLASS}>
                    {unclaimedStores.map((store) => (
                      <StoreCard key={store.id} store={store} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
